<template>
  <div class="post-page">
    <!-- 新增顶部横框 -->
    <div class="post-header">
      <el-button class="back-btn" icon="el-icon-arrow-left" @click="$router.go(-1)" type="text">返回</el-button>
      <div class="user-info">
        <img class="avatar" :src="userInfo.personal?.avatar_small || require('@/assets/profile.png')" alt="头像" />
        <div class="user-info-content">
          <div class="user-info-name">{{ userInfo.personal?.name || '用户昵称' }}</div>
          <div class="user-info-age">{{ userInfo?.id || '账号' }}</div>
        </div>
      </div>
    </div>
    <div class="post-main">
      <!-- 左侧：发帖区 -->
      <div class="post-editor">
        <h2>创建帖子</h2>
        <!-- tips -->
        <div class="tips">
          媒体内容应包括 <b>4张照片或更多</b> / <b>1个视频</b> / <b>1个视频+任意数量的照片</b>。<br />
          文本长度应在 <b>100</b> 到 <b>3500</b> 个字符之间。
        </div>
        <el-input type="textarea" v-model="postContent" :rows="6" placeholder="请输入帖子内容..." maxlength="3500"
          show-word-limit />
        <div class="media-upload">
          <input ref="mediaInput" type="file" multiple accept="image/*,video/*" style="display:none"
            @change="handleMediaChange" />
          <el-button @click="triggerMediaInput" icon="el-icon-picture">添加图片/视频</el-button>
          <div class="media-preview">
            <div v-for="(file, idx) in mediaFiles" :key="idx" class="media-item">
              <img v-if="file.type.startsWith('image')" :src="file.url" class="preview-img" />
              <video v-else controls class="preview-video">
                <source :src="file.lmyUrl" />
              </video>
              <span class="remove-btn" @click="removeMedia(idx)">×</span>
            </div>
          </div>
        </div>
        <el-button type="primary" @click="submitPost" :disabled="!canSubmit">发送</el-button>
      </div>
      <!-- 右侧历史记录及侧边栏 -->
      <div class="post-list" @scroll="handleScroll" ref="postList">
        <h2>历史记录</h2>
        <div v-if="posts.length === 0" class="no-posts">暂无记录</div>
        <div v-for="post in posts" :key="post.id" class="post-item">
          <div class="post-content">{{ post.textPreview || '' }}</div>
          <div class="post-media-list">
            <!-- 图片区 -->
            <div class="media-row" v-if="post.photos && post.photos.length > 0">
              <template v-for="(photo, idx) in (post.photos || []).slice(0, 4)">
                <imagePreview v-if="idx < 3 || (post.photos || []).length <= 4" :key="photo.id" :src="photo.url"
                  :previewSrcList="post.photos || []" :currentIndex="idx" :listFields="'url'" class="post-img" />
                <!-- 第4张为+N遮罩 -->
                <div v-else :key="'photo-more'" class="post-img more-mask"
                  @click="handlePreviewMore(post.photos || [], 3, 'url')" style="position:relative;cursor:pointer;">
                  <img :src="photo.url" class="img-bg" />
                  <div class="mask-text">+{{ (post.photos || []).length - 3 }}</div>
                </div>
              </template>
            </div>
            <!-- 视频区 -->
            <div class="media-row" v-if="post.videos && post.videos.length > 0">
              <template v-for="(video, idx) in (post.videos || []).slice(0, 4)">
                <videoPreview v-if="idx < 3 || (post.videos || []).length <= 4" :key="video.id" :coverSrc="video.url"
                  :previewSrcList="post.videos || []" :currentIndex="idx" :videoId="Number(video.id)" class="post-video" />
                <div v-else :key="'video-more'" class="post-video more-mask"
                  @click="handlePreviewMore(post.videos || [], 3, 'url')" style="position:relative;cursor:pointer;">
                  <video :src="video.url" class="img-bg" />
                  <div class="mask-text">+{{ (post.videos || []).length - 3 }}</div>
                </div>
              </template>
            </div>
          </div>
          <div class="post-time-btns">
            <span class="post-time">{{ formatTime(post.dateSent) }}</span>
            <el-tag :type="(post.isPurchased === true) ? 'success' : 'info'" class="watch-status" size="mini">
              {{ (post.isPurchased === true) ? '已浏览' : '未浏览' }}
            </el-tag>
            <el-button type="text" size="mini" class="view-detail-btn" @click="showPostDetail(post)">浏览</el-button>
          </div>
        </div>
        <div v-if="loadingMore" class="loading-more">加载中...</div>
        <div v-if="noMore && posts.length > 0" class="no-more">数据加载完毕</div>
      </div>
    </div>
    <!-- 帖子详情侧边栏 -->
    <PostDetail :visible="showSidebar" :post="detailPost" @close="showSidebar = false"
      @preview-more="handlePreviewMore" />
    <imagePreview ref="imagePreviewRef" v-show="previewSrcList.length > 0"
      :src="getPreviewSrc(previewSrcList, currentIndex, 'url')" :previewSrcList="previewSrcList"
      :currentIndex="currentIndex" :listFields="'url'" :isPreview="true" :showPreview="false" />
    <attachment ref="attachmentDom"  :chatUserInfo="attachmentUserInfo" @send-sonMediaMessage="getSonMediaMessage" :currentMessageType="currentMessageType" :show-approved-only="false" :show-email-tags="true" :enable-forbidden-tags="true"></attachment>
  </div>
</template>

<script>
import { getDialogMediaPosts, getDialogPost, sendNewPost } from '@/api/talkytimes/message'
import imagePreview from '@/components/imagePreview.vue'
import videoPreview from '@/components/videoPreview.vue'
import { getConnectionsProfiles } from '@/api/talkytimes/personalData'
import PostDetail from './detail.vue'
import attachment from '@/views/talkytimes/views/message/components/attachment.vue'


export default {
  name: 'PostView',
  components: {
    imagePreview,
    videoPreview,
    PostDetail,
    attachment
  },
  data() {
    return {
      postContent: '',
      mediaFiles: [], // {type, url, file}
      posts: [],
      user: {
        id: '', // 用于idRegularUser
        avatar: '',
        nickname: '用户昵称',
        platformUserId: '',
        idInterlocutor: '',
        previewSrcList: [],
        currentIndex: 0
      },
      previewSrcList: [],
      currentIndex: 0,
      showSidebar: false,
      detailPost: {},
      loadingMore: false,
      noMore: false,
      userInfo: {},//聊天用户的资料，不是自己的
      attachmentUserInfo: {},
      currentMessageType: 'postView'
    }
  },
  computed: {
    canSubmit() {
      return this.postContent.trim().length >= 100 && this.postContent.trim().length <= 3500 && (this.mediaFiles.length > 0)
    }
  },
  mounted() {
    console.log('route.query', this.$route.query)
    this.initUserFromRoute()
    this.loadPosts(true);
    this.getUserInfo()
  },
  watch: {
    // 监听路由参数变化
    '$route.query': {
      handler(newQuery, oldQuery) {
        // 检查关键参数是否发生变化
        if (newQuery.platformUserId !== oldQuery?.platformUserId || 
            newQuery.idInterlocutor !== oldQuery?.idInterlocutor) {
          console.log('路由参数发生变化，重新加载数据', newQuery)
          this.resetData()
          this.initUserFromRoute()
          this.loadPosts(true)
          this.getUserInfo()
        }
      },
      deep: true,
      immediate: false
    }
  },
  methods: {
    resetData() {
      // 重置组件数据状态
      this.posts = []
      this.postContent = ''
      this.mediaFiles = []
      this.userInfo = {}
      this.attachmentUserInfo = {}
      this.showSidebar = false
      this.detailPost = {}
      this.loadingMore = false
      this.noMore = false
      this.previewSrcList = []
      this.currentIndex = 0
      this.currentMessageType = 'postView'
    },
    getUserInfo() {
      if (!this.user.idInterlocutor) return;
      getConnectionsProfiles({
        ids: [Number(this.user.idInterlocutor)]
      }).then(res => {
        console.log(res)
        this.userInfo = res.data.profiles[0] || {}
        this.attachmentUserInfo = {
          ...this.userInfo,
          userDetail: this.userInfo,
          userId: Number(this.user.idInterlocutor),
          idInterlocutor: Number(this.user.idInterlocutor)
        }
      })
    },
    initUserFromRoute() {
      // 从路由参数获取platformUserId和idInterlocutor
      const { platformUserId, idInterlocutor } = this.$route.query
      console.log(platformUserId, idInterlocutor, 'platformUserId,idInterlocutor')
      this.user.platformUserId = platformUserId || ''
      this.user.idInterlocutor = idInterlocutor || ''
      // 兼容接口参数
      this.user.id = platformUserId || ''
    },
    getDialogMediaPosts() {
      if (!this.user.idInterlocutor) return;
      console.log(this.user.idInterlocutor, 'this.user.idInterlocutor')
      getDialogMediaPosts({
        idRegularUser: Number(this.user.idInterlocutor),
        idLastPost: 0,
        limit: 15
      }).then(res => {
        console.log(res)
        this.posts = res || []
      })
    },
    triggerMediaInput() {
      // 检查是否已获取到必要的用户ID
      if (!this.user.platformUserId || !this.user.idInterlocutor) {
        this.$message({
          message: '缺少用户信息，无法打开媒体选择窗口',
          type: 'warning'
        });
        return;
      }
      
      // this.$refs.mediaInput.click()
      this.$refs.attachmentDom.init()
    },
    handleMediaChange(e) {
      const files = Array.from(e.target.files)
      files.forEach(file => {
        const type = file.type.startsWith('image') ? 'image' : (file.type.startsWith('video') ? 'video' : null)
        if (!type) return
        const url = URL.createObjectURL(file)
        this.mediaFiles.push({ type, url, file })
      })
      // 清空input以便连续上传同一文件
      e.target.value = ''
    },
    removeMedia(idx) {
      this.mediaFiles.splice(idx, 1)
    },
    submitPost() {
      if (!this.canSubmit || !this.user.idInterlocutor) return
      const newPost = {
        id: Date.now(),
        content: this.postContent,
        media: this.mediaFiles.map(f => ({ type: f.type, url: f.url })),
        createdAt: new Date()
      }
      this.posts.unshift(newPost)
      // return
      sendNewPost({
        idRegularUser: Number(this.user.idInterlocutor),
        idsGalleryPhotos: this.mediaFiles.filter(f => f.type === 'image').map(f => Number(f.id)),
        idsGalleryVideos: this.mediaFiles.filter(f => f.type === 'video').map(f => Number(f.id)),
        text: this.postContent
      }).then(res => {
        // this.postContent = ''
        console.log(res.idMessage)
        if(res.idMessage){
          // 发送成功后重置编辑器数据
          this.postContent = '';
          this.mediaFiles = [];
          
          this.$message({
            message: '发送成功',
            type: 'success'
          });
        }else{
          this.$message({
            message: '发送失败,'+res.error,
            type: 'error'
          });
        }
      })
    },
    formatTime(date) {
      if (!date) return '';
      const d = new Date(date)
      return d.toLocaleString()
    },
    handlePreviewMore(params) {
      // 如果是从子组件传来的参数对象，解构参数
      if (params && typeof params === 'object' && params.list) {
        const { list, index } = params
        this.previewSrcList = list
        this.currentIndex = index
      } else {
        // 兼容原有的直接传参方式
        this.previewSrcList = arguments[0]
        this.currentIndex = arguments[1]
      }
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.$refs.imagePreviewRef && this.$refs.imagePreviewRef.openPreview) {
            this.$refs.imagePreviewRef.openPreview()
          }
        }, 0)
      })
    },
    getPreviewSrc(list, idx, field) {
      if (!Array.isArray(list) || !list[idx]) return '';
      return field ? list[idx][field] : list[idx];
    },
    showPostDetail(post) {
      if (!this.user.idInterlocutor) return;
      this.detailPost = {}
      getDialogPost({
        idPost: Number(post.id || 0),
        idInterlocutor: Number(this.user.idInterlocutor)
      }).then(res => {
        console.log(res)
        this.detailPost = res
      })
      this.showSidebar = true
    },
    loadPosts(isInit = false) {
      if (this.loadingMore || this.noMore || !this.user.idInterlocutor) return;
      this.loadingMore = true;
      const idLastPost = isInit || this.posts.length === 0 ? 0 : (this.posts[this.posts.length - 1]?.id || 0);
      getDialogMediaPosts({
        idRegularUser: Number(this.user.idInterlocutor),
        idLastPost,
        limit: 15
      }).then(res => {
        if (isInit) {
          this.posts = res || [];
        } else {
          if (res && res.length) {
            this.posts = this.posts.concat(res);
          }
        }
        if (!res || res.length < 15) {
          this.noMore = true;
        }
      }).finally(() => {
        this.loadingMore = false;
      });
    },
    handleScroll() {
      const el = this.$refs.postList;
      if (!el) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
        this.loadPosts(false);
      }
    },
    getSonMediaMessage(data){
      this.$refs.attachmentDom.visible = false
      
      console.log('data',data)
      // 将data.dataMsgList中的数据添加到mediaFiles中
      const formatted = (data.dataMsgList || []).map(item => {
        if (item.fileCateType === 1) {
          // 图片
          return {
            type: 'image',
            url: item.url,
            originalName: item.originalName,
            id: Number(item.idPhoto || item.id), // 确保 id 是数字类型
            ...item
          };
        } else if (item.fileCateType === 2) {
          // 视频
          return {
            type: 'video',
            lmyUrl: item.urlHd || item.urlSd || '',
            originalName: item.originalName,
            id: Number(item.idVideo || item.id), // 确保 id 是数字类型
            ...item
          };
        }
        // 不支持音频，其他类型不处理
        return null;
      }).filter(Boolean);
      this.mediaFiles = [...this.mediaFiles, ...formatted];
      console.log('this.mediaFiles',this.mediaFiles)
    }
  }
}
</script>

<style lang="scss" scoped>
.post-page {
  padding: 0;
  background: none;
  min-height: 100vh;

  .post-header {
    display: flex;
    align-items: center;
    height: 56px;
    background: #fff;
    border-bottom: 1px solid #f0f0f0;
    padding: 0 32px;
    margin-bottom: 16px;

    .back-btn {
      margin-right: 24px;
      font-size: 16px;
    }

    .user-info {
      display: flex;
      align-items: center;

      .avatar {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        object-fit: cover;
        margin-right: 10px;
      }

      .nickname {
        font-size: 18px;
        color: #333;
        font-weight: 500;
      }
    }
  }

  .post-main {
    display: flex;
    gap: 24px;
    width: 100%;
    max-width: none;
    margin: 0;
    align-items: flex-start;
  }

  .post-editor {
    flex: 1 1 0;
    background: #fff;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    padding: 24px;
    min-width: 320px;
    box-sizing: border-box;

    .tips {
      background: #f8f8f8;
      color: #666;
      font-size: 14px;
      border-radius: 6px;
      padding: 10px 14px;
      margin-bottom: 12px;
      line-height: 1.7;
    }

    .media-upload {
      margin: 16px 0;

      .media-preview {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        margin-top: 10px;

        .media-item {
          position: relative;
          width: 80px;
          height: 80px;

          .preview-img,
          .preview-video {
            width: 100%;
            height: 100%;
            object-fit: cover;
            border-radius: 6px;
            border: 1px solid #eee;
          }

          .remove-btn {
            position: absolute;
            top: -8px;
            right: -8px;
            background: #f56c6c;
            color: #fff;
            border-radius: 50%;
            width: 20px;
            height: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 14px;
            cursor: pointer;
            border: 2px solid #fff;
          }
        }
      }
    }
  }

  .post-list {
    flex: 1 1 0;
    background: #fff;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    padding: 24px;
    min-width: 320px;
    max-height: 80vh;
    overflow-y: auto;
    box-sizing: border-box;

    .no-posts {
      color: #aaa;
      text-align: center;
      margin-top: 40px;
    }

    .post-item {
      border-bottom: 1px solid #f0f0f0;
      padding: 16px 0;

      &:last-child {
        border-bottom: none;
      }

      .post-content {
        font-size: 16px;
        margin-bottom: 8px;
        color: #333;
        word-break: break-all;
      }

      .post-media-list {
        display: block;

        .post-img,
        .post-video {
          width: 80px;
          height: 80px;
          object-fit: cover;
          border-radius: 6px;
          border: 1px solid #eee;
        }
      }

      .post-time-btns {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-top: 4px;

        .post-time {
          color: #aaa;
          font-size: 12px;
        }

        .watch-status {
          margin-left: 8px;
        }

        .view-detail-btn {
          margin-left: 8px;
          color: #409EFF;
          cursor: pointer;
          font-size: 12px;
          padding: 0 6px;
        }
      }
    }
  }
}

.post-list-and-detail {
  display: flex;
  flex-direction: row;
  width: 100%;
}

.media-row {
  display: flex;
  gap: 8px;
  margin-bottom: 6px;
}

.more-mask {
  position: relative;
  overflow: hidden;
  border-radius: 10px;
}

.img-bg {
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: brightness(0.6);
}

.mask-text {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 2em;
  font-weight: bold;
  background: rgba(0, 0, 0, 0.4);
}



.loading-more {
  text-align: center;
  color: #888;
  padding: 12px 0;
  font-size: 15px;
}

.no-more {
  text-align: center;
  color: #aaa;
  padding: 12px 0 18px 0;
  font-size: 15px;
}

.user-info-name {
  font-weight: 600;
}
::v-deep .el-dialog {
	border-radius: 30px;
}

::v-deep .el-dialog__header {
	padding: 0;
	z-index: 1 !important;
}

::v-deep .el-dialog__body {
	padding: 24px;
}
::v-deep .el-dialog {
		// height: 100%;
    margin-top: 5vh !important;
		.el-dialog__body {
			height: calc(90vh - 5px);
			box-sizing: border-box;
		}
	}
</style>
